"use client";

import type { Dispatch, SetStateAction } from "react";
import { useState } from "react";
import type { IconType } from "react-icons/lib";
import { LuChevronDown } from "react-icons/lu";

import { Button } from "@/components/ui/button";
import {
  PopoverDrawer,
  PopoverDrawerContent,
  PopoverDrawerTrigger,
} from "@/components/ui/popover-drawer";
import { Switch } from "@/components/ui/switch";
import type { PostCategoryFilter } from "@/contexts/feed-context";
import { useFeedInternalContext } from "@/contexts/feed-context";

type FilterItemType = {
  icon: IconType;
  label: string;
  postCategory: string;
};

export const FeedFilter = ({
  items,
  filter,
  setFilter,
}: {
  items: FilterItemType[];
  filter: Record<string, boolean>;
  setFilter: Dispatch<SetStateAction<Record<string, boolean>>>;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <PopoverDrawer
      historyName="feed-content-filter"
      onOpenChange={setOpen}
      open={open}
      title="Content filter"
    >
      <PopoverDrawerTrigger asChild>
        <Button variant="link" className="flex items-center gap-1 text-muted">
          <span className="text-xs font-normal">Content</span>
          <LuChevronDown />
        </Button>
      </PopoverDrawerTrigger>
      <PopoverDrawerContent
        side="bottom"
        drawerDescription="Choose which feed content categories are visible."
        className="
          custom-scrollbar max-h-[60vh] w-screen overflow-y-auto p-6 shadow-xl
          md:max-h-[400px] md:w-[400px]
        "
      >
        <div className="flex flex-col">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex text-sm text-muted">
              What content would you like to see?
            </h3>
          </div>
          <div className="flex flex-col gap-4">
            {items.map((item) => (
              <FeedFilterItem
                {...item}
                key={item.label}
                {...{
                  filter,
                  setFilter,
                }}
              />
            ))}
          </div>
        </div>
      </PopoverDrawerContent>
    </PopoverDrawer>
  );
};

type FeedFilterItemProps<T extends Record<C, boolean>, C extends string> = {
  icon: IconType;
  label: string;
  filter: T;
  setFilter: Dispatch<SetStateAction<Record<PostCategoryFilter, boolean>>>;
  postCategory: C;
};

const FeedFilterItem = <T extends Record<C, boolean>, C extends string>({
  icon: Icon,
  label,
  postCategory,
  setFilter,
  filter,
}: FeedFilterItemProps<T, C>) => {
  const { setPosts } = useFeedInternalContext();
  const checked = filter[postCategory];

  const onCheckedChange = (enabled: boolean) => {
    setFilter((state) => {
      return {
        ...state,
        [postCategory]: enabled,
      } as Record<PostCategoryFilter, boolean>;
    });
    setPosts([]);
  };

  return (
    <label
      className="
        flex w-full items-center justify-between rounded-3xl bg-card p-2 px-4
        text-sm
      "
      htmlFor={label}
    >
      <div className="flex items-center gap-2">
        <span className="text-muted">
          <Icon />
        </span>
        <span className="font-semibold text-muted-foreground">{label}</span>
      </div>
      <div>
        <Switch {...{ checked, onCheckedChange, id: label }} />
      </div>
    </label>
  );
};
